import * as React from "react";
import { Link, useParams } from "react-router-dom";

import {
  ApiError,
  getAdminPageBookings,
  getAdminUserProfile,
  getUserBookingPages,
  getUserInterest,
  setUserTier,
} from "@/api/client";
import { PageTitle } from "@/components/PageTitle";
import type {
  BookingPageView,
  BookingView,
  PreviewInterest,
  ProfileView,
  Tier,
} from "@/api/types";
import { TIERS } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { ComicPanel } from "@/components/comic";

import { ProfileForm } from "../profile/ProfileForm";

/**
 * Admin-only, read-only view of another user's profile at
 * {@code /admin/users/:userId}. Renders the SAME {@link ProfileForm} the user
 * sees on their own profile page, but with {@code readOnly} — every control
 * disabled, no Save button — so an admin sees exactly what the user
 * configured without being able to change it. Opened in a new tab from the
 * admin table.
 *
 * <p>Authorization is the backend admin_user allowlist: a non-admin gets a
 * 403 from {@code GET /admin/users/:id} and the error panel below.</p>
 */
export function AdminUserProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const [profile, setProfile] = React.useState<ProfileView | null>(null);
  const [interest, setInterest] = React.useState<PreviewInterest[]>([]);
  const [tier, setTier] = React.useState<Tier | null>(null);
  const [savingTier, setSavingTier] = React.useState(false);
  const [pages, setPages] = React.useState<BookingPageView[]>([]);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setProfile(null);
    setInterest([]);
    setPages([]);
    setError(null);
    getUserInterest(userId)
      .then((t) => {
        if (!cancelled) setInterest(t);
      })
      .catch(() => {
        /* non-fatal: profile still renders */
      });
    getUserBookingPages(userId)
      .then((p) => {
        if (!cancelled) setPages(p);
      })
      .catch(() => {
        /* non-fatal */
      });
    getAdminUserProfile(userId)
      .then((p) => {
        if (!cancelled) {
          setProfile(p);
          setTier(p.tier);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          err instanceof ApiError && err.status === 403
            ? "Your account is not on the admin allowlist."
            : err instanceof ApiError && err.status === 404
              ? "No such user."
              : err instanceof Error
                ? err.message
                : String(err),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const changeTier = async (next: Tier) => {
    if (!userId) return;
    const previous = tier;
    setTier(next);
    setSavingTier(true);
    try {
      const updated = await setUserTier(userId, { tier: next });
      setTier(updated.tier as Tier);
    } catch {
      setTier(previous);
    } finally {
      setSavingTier(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-6 font-comic-body">
      <header className="flex items-start justify-between gap-3">
        <div>
          <Link
            to="/admin"
            className="font-comic-headline text-xs uppercase tracking-wide text-muted-foreground hover:underline"
          >
            ← back to admin
          </Link>
          <PageTitle className="mt-1">
            {profile?.handle ? `@${profile.handle}` : "User profile"}
          </PageTitle>
          <p className="text-xs text-muted-foreground">
            {profile ? profile.email : "Read-only view"}
            {profile && (
              <>
                {" · "}
                <StatusBadge status={profile.approvalStatus} />
                {profile.admin && (
                  <Badge variant="secondary" className="ml-2">
                    admin
                  </Badge>
                )}
              </>
            )}
          </p>
          <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
            Read-only admin view
          </p>
          {profile?.handle && (
            <a
              href={`/@${profile.handle}`}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-block font-comic-headline text-xs uppercase tracking-wide text-foreground underline-offset-2 hover:underline"
            >
              View @{profile.handle}&apos;s public card →
            </a>
          )}
        </div>
      </header>

      {error ? (
        <ComicPanel title="Error">
          <p className="text-sm text-destructive">{error}</p>
        </ComicPanel>
      ) : !profile ? (
        <p className="text-sm text-muted-foreground">Loading profile…</p>
      ) : (
        <>
          {interest.length > 0 && (
            <ComicPanel title="Preview interest" loud={String(interest.length)}>
              <ul className="space-y-2">
                {interest.map((note) => (
                  <li
                    key={note.id}
                    className="rounded-md border-2 border-black bg-muted px-3 py-2 text-sm"
                  >
                    <p className="leading-snug">“{note.message}”</p>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {formatWhen(note.createdAt)}
                    </p>
                  </li>
                ))}
              </ul>
            </ComicPanel>
          )}
          <ComicPanel title="Subscription tier">
            <div className="flex items-center gap-3">
              <select
                value={tier ?? "FREE"}
                disabled={savingTier}
                onChange={(e) => changeTier(e.target.value as Tier)}
                className="h-10 rounded-md border-2 border-black bg-background px-3 text-sm"
              >
                {TIERS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              {savingTier && <span className="text-xs text-muted-foreground">saving…</span>}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Gates their booking-page caps: active pages, bookings per page, and link lifetime.
            </p>
          </ComicPanel>

          <BookingPagesSection pages={pages} />

          <ProfileForm profile={profile} readOnly />
        </>
      )}
    </div>
  );
}

/** A user's booking pages, each expandable to its bookings (recipients). */
function BookingPagesSection({ pages }: { pages: BookingPageView[] }) {
  const [open, setOpen] = React.useState<string | null>(null);
  const [bookings, setBookings] = React.useState<Record<string, BookingView[]>>({});

  const toggle = async (id: string) => {
    if (open === id) {
      setOpen(null);
      return;
    }
    if (!bookings[id]) {
      try {
        const list = await getAdminPageBookings(id);
        setBookings((prev) => ({ ...prev, [id]: list }));
      } catch {
        /* ignore — the row still expands to "no bookings" */
      }
    }
    setOpen(id);
  };

  if (pages.length === 0) {
    return (
      <ComicPanel title="Booking pages">
        <p className="text-sm text-muted-foreground">This user has no booking pages.</p>
      </ComicPanel>
    );
  }
  return (
    <ComicPanel title="Booking pages" loud={String(pages.length)}>
      <ul className="space-y-2">
        {pages.map((p) => (
          <li key={p.id} className="rounded-md border-2 border-black bg-muted px-3 py-2 text-sm">
            <div className="flex flex-wrap items-center gap-x-3">
              <span className="font-semibold">{p.title}</span>
              <span className="text-xs text-muted-foreground">
                {p.slot_minutes} min · {p.status.toLowerCase()}
              </span>
              <button
                type="button"
                className="text-xs text-brand-blue hover:underline"
                onClick={() => toggle(p.id)}
              >
                {p.booking_count} booked
              </button>
            </div>
            {open === p.id && (
              <ul className="mt-2 space-y-1 border-t-2 border-dashed border-black/20 pt-2 text-xs">
                {(bookings[p.id] ?? []).length === 0 ? (
                  <li className="italic text-muted-foreground">No bookings.</li>
                ) : (
                  (bookings[p.id] ?? []).map((b) => (
                    <li key={b.id} className="flex flex-wrap gap-x-3">
                      <span className="font-medium">{formatWhen(b.start)}</span>
                      <span>{b.invitee_name ?? "—"}</span>
                      <span className="text-muted-foreground">{b.invitee_email}</span>
                    </li>
                  ))
                )}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </ComicPanel>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}

function StatusBadge({ status }: { status: ProfileView["approvalStatus"] }) {
  const variant =
    status === "approved" ? "default" : status === "disabled" ? "destructive" : "secondary";
  return <Badge variant={variant}>{status}</Badge>;
}
